import clsx from "clsx";

export type TabKey = "overview" | "underAttackMode" | "campaigns" | "investigations" | "trends" | "scorerHealth";

export const TABS: { key: TabKey; label: string; description: string; icon: JSX.Element; incident?: boolean }[] = [
  { key: "overview",       label: "Overview",       description: "Live risk pulse, top high-risk events, and global activity", icon: <Icon path="M3 12l9-9 9 9M5 10v10h14V10" /> },
  { key: "underAttackMode", label: "Under Attack Mode", description: "Incident-only multi-signal analysis and rule recommendations", incident: true, icon: <Icon path="M12 2l8 4v6c0 5-3.4 9.4-8 10-4.6-.6-8-5-8-10V6l8-4zM12 7v6M12 17h.01" /> },
  { key: "campaigns",      label: "Active Campaigns", description: "Correlated risk clusters in flight", icon: <Icon path="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" /> },
  { key: "investigations", label: "Investigations", description: "Search and drill into risk events", icon: <Icon path="M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35" /> },
  { key: "trends",         label: "Trends",         description: "Patterns across time, geography, and entities", icon: <Icon path="M3 17l6-6 4 4 8-8M14 7h7v7" /> },
  { key: "scorerHealth",   label: "Scorer Health",  description: "Durable Object RPC latency and hot keys", icon: <Icon path="M22 12h-4l-3 9L9 3l-3 9H2" /> },
];

function Icon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

export function Sidebar({ active, onNavigate }: { active: TabKey; onNavigate: (k: TabKey) => void }) {
  return (
    <nav className="hidden w-60 shrink-0 border-r border-border bg-bg lg:flex lg:flex-col">
      <ul className="flex flex-col gap-0.5 p-3">
        {TABS.map((t) => (
          <li key={t.key}>
            <button
              type="button"
              onClick={() => onNavigate(t.key)}
              title={t.description}
              className={clsx(
                "group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                t.incident
                  ? active === t.key
                    ? "border border-sev-high/50 bg-sev-high/20 text-fg shadow-card"
                    : "border border-sev-high/30 bg-sev-high/10 text-sev-high hover:bg-sev-high/15 hover:text-fg"
                  : active === t.key
                    ? "bg-surface-2 text-fg"
                    : "text-fg-muted hover:bg-surface-1 hover:text-fg",
              )}
            >
              <span className={clsx(
                "shrink-0",
                t.incident
                  ? "text-sev-high"
                  : active === t.key ? "text-accent" : "text-fg-subtle group-hover:text-fg-muted",
              )}>{t.icon}</span>
              <span className="font-medium">{t.label}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-auto border-t border-border p-3 text-[11px] leading-relaxed text-fg-subtle">
        <p>v1 — log-only.</p>
        <p>Internal use, behind WAF allowlist.</p>
      </div>
    </nav>
  );
}

/** Mobile / narrow nav — used as a fallback. */
export function TopNav({ active, onNavigate }: { active: TabKey; onNavigate: (k: TabKey) => void }) {
  return (
    <div className="border-b border-border bg-bg px-2 lg:hidden">
      <div className="flex gap-1 overflow-x-auto py-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onNavigate(t.key)}
            className={clsx(
              "shrink-0 rounded-md px-3 py-1.5 text-sm",
              t.incident
                ? active === t.key
                  ? "border border-sev-high/50 bg-sev-high/20 text-fg"
                  : "border border-sev-high/30 bg-sev-high/10 text-sev-high hover:bg-sev-high/15 hover:text-fg"
                : active === t.key ? "bg-surface-2 text-fg" : "text-fg-muted hover:text-fg",
            )}
          >{t.label}</button>
        ))}
      </div>
    </div>
  );
}
